import { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { openStorePage } from "@/lib/externalLinks";
import { currentPlatform } from "@/lib/clientVersion/initClientSignature";
import { getNativeAppVersion } from "@/lib/clientVersion/nativeAppVersion";
import type { ClientPlatform } from "@/lib/clientVersion/signature";
import {
  fetchMinVersion,
  isNativeUpdateRequired,
  MIN_VERSION_CHECK_INTERVAL_MS,
  STORE_URLS,
} from "@/lib/appUpdate/nativeMinVersion";
import { UpdateBannerShell } from "./UpdateBannerShell";

interface Props {
  platform?: ClientPlatform;
  check?: () => Promise<boolean>;
  now?: () => number;
}

/**
 * Rappel de mise à jour des apps natives. Monté une fois (App.tsx). Compare la
 * version native au minimum de app_min_versions, au démarrage puis à chaque
 * retour au premier plan (cinq minutes au moins entre deux lectures). Sous le
 * minimum : bandeau SANS fermeture, qui laisse l'app utilisable (décision de
 * Julien du 2026-10-12), et qui reste jusqu'au redémarrage. Sur le web : rien,
 * AppUpdateWatcher s'en charge.
 */
export function NativeUpdateReminder({ platform = currentPlatform(), check, now = Date.now }: Props = {}) {
  const { t } = useLanguage();
  const [required, setRequired] = useState(false);
  const lastCheckAt = useRef<number | null>(null);
  const nativePlatform = platform === "ios" || platform === "android" ? platform : null;

  const runCheck = useMemo(
    () =>
      check ??
      (() =>
        nativePlatform
          ? isNativeUpdateRequired({
              getCurrentVersion: getNativeAppVersion,
              fetchMin: () => fetchMinVersion(nativePlatform),
            })
          : Promise.resolve(false)),
    [check, nativePlatform],
  );

  useEffect(() => {
    if (!nativePlatform) return;
    let cancelled = false;
    let removeListener: (() => void) | undefined;

    const verify = () => {
      const current = now();
      if (lastCheckAt.current !== null && current - lastCheckAt.current < MIN_VERSION_CHECK_INTERVAL_MS) return;
      lastCheckAt.current = current;
      runCheck()
        .then((isRequired) => {
          // Ne redescend jamais : une fois dû, le rappel reste jusqu'au redémarrage.
          if (!cancelled && isRequired) setRequired(true);
        })
        .catch(() => {});
    };

    verify();

    // Même motif d'écoute que src/lib/auth/nativeAutoRefresh.ts.
    import("@capacitor/app")
      .then(({ App }) =>
        App.addListener("appStateChange", (state) => {
          if (state.isActive) verify();
        }),
      )
      .then((listener) => {
        if (cancelled) listener.remove();
        else removeListener = () => listener.remove();
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, [nativePlatform, runCheck, now]);

  if (!required || !nativePlatform) return null;

  return (
    <UpdateBannerShell
      message={t("appUpdate.nativeMessage")}
      actionLabel={t("appUpdate.openStore")}
      onAction={() => openStorePage(STORE_URLS[nativePlatform])}
      spacerTestId="native-update-spacer"
    />
  );
}
